import { useEffect } from "react";

import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { VideoToolTip } from "@components/video-tooltip";
import {
  useChartState,
  type MainMetric,
} from "@features/sales-v3/hooks/use-chart-state";
import {
  CustomMetricSearch,
  getExcludedFrequentlyUsedMetrics,
  getFrequentlyUsedMetrics,
} from "@features/sales-v3/utils/frequently-used-metrics";
import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { SelectWithGroupedOptions } from "@shared/selects";

import { MetricFilters } from "./metric-filters";

interface MainMetricSelectProps {
  chartIndex: number;
  metrics: SalesPageMetric[];
  mainMetric: MainMetric;
  isBarChart: boolean;
  disabled?: boolean;
}

export const MainMetricSelect = ({
  chartIndex,
  metrics,
  mainMetric,
  isBarChart,
  disabled = false,
}: MainMetricSelectProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    chartConfigs,
    updateMainMetricFromChartAt,
    updateCompareFromChartAt,
    getChartAt,
  } = useChartState();
  const chartConfig = chartConfigs[chartIndex];

  const handleMetricChange = (metric: SalesPageMetric) => {
    prettyLogEventPage({
      domain: "Charts",
      action: "onSelect",
      additionalActionInfo: "Select Chart Main Metric",
      data: {
        pageContext: { metricId: metric.id },
      },
    });

    updateMainMetricFromChartAt(chartIndex, { metricName: metric.id });
  };

  // Fixes the edge case where switching between accounting modes would change available metrics
  // defaults to spend if the metric
  useEffect(() => {
    if (!metrics.find(({ id }) => id === mainMetric.metricName)) {
      updateMainMetricFromChartAt(chartIndex, {
        metricName: "spend",
        filters: mainMetric.filters,
      });
    }
  }, [metrics, mainMetric, chartIndex, updateMainMetricFromChartAt]);

  const chart = getChartAt(chartIndex);
  const updateMetricFilters = (
    metricFilters: MainMetric["filters"],
    ignoreTableFilters: MainMetric["ignoreTableFilters"],
  ) => {
    updateMainMetricFromChartAt(chartIndex, {
      filters: metricFilters,
      ignoreTableFilters,
    });

    chartConfig.comparisons.forEach((comparison, compareIndex) => {
      updateCompareFromChartAt(
        { chartIndex, compareIndex },
        { ...comparison, filters: metricFilters },
      );
    });
  };

  const legend = buildLegend(
    {
      borderColor: "#7081FF",
      backgroundColor: "#7081FF",
      borderDash: [],
    },
    isBarChart,
  );

  const frequentlyUsedMetrics = getFrequentlyUsedMetrics(metrics);
  const otherMetrics = getExcludedFrequentlyUsedMetrics(metrics);

  return (
    <div className="flex flex-col items-start">
      <div className="flex items-center space-between w-full mb-1">
        <div
          className={clsx("text-xs flex-grow flex items-center", {
            "opacity-60 pointer-events-none": disabled,
          })}
        >
          METRIC
          <VideoToolTip
            videoUrl="https://www.youtube.com/embed/75L5sMFesTU?si=prftxz8lWgcRzIvY"
            contentDetails={{
              title: "Amazon performance",
              content:
                "A guide to help you analyze the impact of your advertising spend across " +
                "different channels on Amazon revenue.",
            }}
          />
        </div>
        <div dangerouslySetInnerHTML={{ __html: legend }} />
      </div>
      <SelectWithGroupedOptions
        isDisabled={disabled}
        width="180px"
        CustomSearchClass={CustomMetricSearch}
        onChange={handleMetricChange}
        isSearchable
        value={metrics.find(({ id }) => id === mainMetric.metricName)!}
        groupedOptions={[
          {
            label: "Frequently Used",
            options: frequentlyUsedMetrics.map((metric) => ({
              label: metric.name,
              value: metric,
            })),
          },
          {
            options: otherMetrics.map((metric) => ({
              label: metric.name,
              value: metric,
            })),
          },
        ]}
      />
      <MetricFilters
        metricFilters={chart.mainMetric.filters}
        ignoreTableFilters={chart.mainMetric.ignoreTableFilters}
        updateMetricFilters={updateMetricFilters}
        isAddFilterDisabled={disabled}
      />
    </div>
  );
};
